import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Tag } from '@captain-tasko/shared';
import { TAG_COLOR_PALETTE } from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { TagBadge } from '../components/TagBadge.js';
import { EmptyState } from '../components/EmptyState.js';
import { toast } from '../components/Toast.js';
import { SortableTh } from '../components/SortableTh.js';
import { useTableSort } from '../lib/sort.js';
import { AuditHistoryDrawer } from '../components/AuditHistoryDrawer.js';
// Lucide icons on Edit/Delete to match the look established on
// TaggingRulesAdmin. size=13 / strokeWidth=1.75 reads light and tight.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — installed via pnpm install
import { Pencil, Trash2, History } from 'lucide-react';

export function TagsAdmin({ onNavigate }: { onNavigate: (p: string) => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: () => apiList<Tag>('/tags'),
  });
  const [showDialog, setShowDialog] = useState<{ mode: 'create' } | { mode: 'edit'; tag: Tag } | null>(null);
  // Fast-follow (CTSK-389) — tags has no per-resource detail route, so
  // the History affordance opens AuditHistoryDrawer directly off the
  // row instead of a detail-page tab (same drawer component
  // DocumentDetail.tsx / StorageContainerDetail.tsx use).
  const [historyTag, setHistoryTag] = useState<Tag | null>(null);

  // ctsk-069 — client-side substring search across name + description.
  // Pure client because the tags list is typically small (< few hundred)
  // and the server doesn't need a new query param for this.
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const rows = data?.data ?? [];
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((t) =>
      t.name.toLowerCase().includes(needle) ||
      (t.description ?? '').toLowerCase().includes(needle),
    );
  }, [data?.data, q]);

  const { sorted, sort, setSort } = useTableSort(filtered, {
    name: (t: Tag) => t.name,
    description: (t) => t.description ?? '',
    documentCount: (t) => t.documentCount,
    createdAt: (t) => t.createdAt,
  }, { key: 'name', dir: 'asc' });

  const del = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/tags/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Tag deleted'); qc.invalidateQueries({ queryKey: ['tags'] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Tags</h1>
          <div className="page-subtitle">
            {data?.data.length ?? 0} tags
            {q.trim() && data && (
              <span> · showing {filtered.length}</span>
            )}
          </div>
        </div>
        <button className="btn btn--create" onClick={() => setShowDialog({ mode: 'create' })}>+ Create tag</button>
      </div>

      {/* ctsk-069 — substring search across name + description. */}
      {data && data.data.length > 0 && (
        <div className="toolbar" style={{ flexWrap: 'wrap' }}>
          <input
            type="search"
            placeholder="Search tags…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              flex: 1,
              maxWidth: 360,
            }}
          />
          {q.trim() && (
            <button className="btn btn--ghost" onClick={() => setQ('')}>
              Clear
            </button>
          )}
        </div>
      )}

      {isLoading && <SkeletonRows rows={5} />}
      {data && data.data.length === 0 && (
        <EmptyState
          title="No tags yet"
          body="Tags help organize documents. Create one to get started."
          action={<button className="btn btn--create" onClick={() => setShowDialog({ mode: 'create' })}>+ Create tag</button>}
        />
      )}
      {data && data.data.length > 0 && filtered.length === 0 && (
        <EmptyState
          title="No matches"
          body={`No tag matches "${q}". Try a shorter search or clear it.`}
          action={<button className="btn" onClick={() => setQ('')}>Clear search</button>}
        />
      )}
      {data && filtered.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              <SortableTh sortKey="name" sort={sort} onSortChange={setSort}>Tag</SortableTh>
              <SortableTh sortKey="description" sort={sort} onSortChange={setSort}>Description</SortableTh>
              <SortableTh sortKey="documentCount" sort={sort} onSortChange={setSort}>Documents</SortableTh>
              <SortableTh sortKey="createdAt" sort={sort} onSortChange={setSort}>Created</SortableTh>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((t) => (
              <tr key={t.id}>
                {/* Click the tag chip to open the Documents page
                    pre-filtered by this tag. Restores the behavior
                    the user had before TagBadge lost its onClick. */}
                <td>
                  <TagBadge
                    tag={t}
                    title={`Open documents tagged "${t.name}"`}
                    onClick={(tag) => onNavigate(`/documents?tag=${encodeURIComponent(tag.id)}`)}
                  />
                </td>
                <td>{t.description ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td>{t.documentCount}</td>
                <td>{new Date(t.createdAt).toLocaleDateString()}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
                    <button
                      className="btn btn--ghost"
                      onClick={() => setShowDialog({ mode: 'edit', tag: t })}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <Pencil size={13} strokeWidth={1.75} aria-hidden /> Edit
                    </button>
                    <button
                      className="btn btn--ghost"
                      onClick={() => setHistoryTag(t)}
                      title={`View audit history for "${t.name}"`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <History size={13} strokeWidth={1.75} aria-hidden /> History
                    </button>
                    <button
                      className="btn btn--ghost"
                      onClick={() => { if (confirm(`Delete tag "${t.name}"?`)) del.mutate(t.id); }}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--danger-primary)' }}
                    >
                      <Trash2 size={13} strokeWidth={1.75} aria-hidden /> Delete
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showDialog && (
        <TagDialog
          tag={showDialog.mode === 'edit' ? showDialog.tag : null}
          onCancel={() => setShowDialog(null)}
          onSaved={() => {
            setShowDialog(null);
            qc.invalidateQueries({ queryKey: ['tags'] });
          }}
        />
      )}

      {historyTag && (
        <AuditHistoryDrawer
          resourceType="tag"
          resourceId={historyTag.id}
          onClose={() => setHistoryTag(null)}
        />
      )}
    </>
  );
}

function TagDialog({
  tag,
  onCancel,
  onSaved,
}: {
  tag: Tag | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(tag?.name ?? '');
  const [color, setColor] = useState<string>(tag?.color ?? TAG_COLOR_PALETTE[0]!);
  const [description, setDescription] = useState(tag?.description ?? '');

  const save = useMutation({
    mutationFn: () => {
      const body = { name, color, description: description || null };
      return tag
        ? apiSingle<Tag>(`/tags/${tag.id}`, { method: 'PATCH', body })
        : apiSingle<Tag>('/tags', { method: 'POST', body });
    },
    onSuccess: () => { toast.success(tag ? 'Tag updated' : 'Tag created'); onSaved(); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div
      role="dialog"
      aria-labelledby="tag-dlg-title"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.3)', display: 'grid', placeItems: 'center', zIndex: 2000 }}
      onClick={onCancel}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface-raised)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', width: 480 }}>
        <h2 id="tag-dlg-title" style={{ margin: 0, marginBottom: 16 }}>{tag ? `Edit tag: ${tag.name}` : 'New tag'}</h2>

        <div className="field">
          <label htmlFor="tag-name">Name</label>
          <input id="tag-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>

        <div className="field">
          <label htmlFor="tag-desc">Description</label>
          <textarea
            id="tag-desc"
            value={description ?? ''}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="What is this tag for? Optional."
            style={{ padding: 8, border: '1px solid var(--border-strong)', borderRadius: 4, fontFamily: 'inherit', resize: 'vertical' }}
          />
        </div>

        <div className="field">
          <label>Color</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {TAG_COLOR_PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Color ${c}`}
                onClick={() => setColor(c)}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 4,
                  border: `2px solid ${c === color ? 'var(--text-primary)' : 'transparent'}`,
                  background: c,
                  cursor: 'pointer',
                  padding: 0,
                }}
              />
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn btn--primary" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : (tag ? 'Save changes' : 'Create tag')}
          </button>
        </div>
      </div>
    </div>
  );
}
